import type { Store } from '@tagadapay/crm-entities';
import type { LucideIcon } from 'lucide-react';
import { FlaskConical, Leaf, MoreHorizontal, Shirt, Sparkles, Users } from 'lucide-react';

/**
 * Guided funnel-generation catalog for the final setup step.
 *
 * The merchant answers one question — "what do you sell?" (the vertical) — and an
 * optional "add a one-click upsell?" toggle. The vertical is persisted onto the store
 * (`stores.updateVertical`) AND drives everything derived here (checkout theme,
 * thank-you page, upsell design, funnel shape) — fed into the studio auto-deploy
 * pipeline (`useFunnelCreation`) as a `templateId` + per-step `starterOverrides` map.
 */

/** Allowed verticals; aliased to the shared `StoreVertical` so client + DB stay in lockstep. */
export type SetupFunnelVertical = Store.StoreVertical;

export type SetupFunnelTheme = 'dark' | 'normal';

/**
 * Product kind a vertical sells, used to filter the list by store kind:
 * - `physical` — shipped goods; hidden from digital stores.
 * - `digital` — no shipping (e.g. a pure service); hidden from physical stores.
 * - `any` — catch-all ("everything else"); always shown.
 */
export type SetupFunnelVerticalKind = 'physical' | 'digital' | 'any';

export interface SetupFunnelVerticalOption {
  id: SetupFunnelVertical;
  nameKey: string;
  hintKey: string;
  icon: LucideIcon;
  kind: SetupFunnelVerticalKind;
}

/** Q1 options, in display order. i18n lives under `experience.checkout.vertical.options.<id>`. */
export const SETUP_FUNNEL_VERTICAL_OPTIONS: SetupFunnelVerticalOption[] = [
  {
    id: 'peptides',
    nameKey: 'experience.checkout.vertical.options.peptides.name',
    hintKey: 'experience.checkout.vertical.options.peptides.hint',
    icon: FlaskConical,
    kind: 'physical',
  },
  {
    id: 'nutra',
    nameKey: 'experience.checkout.vertical.options.nutra.name',
    hintKey: 'experience.checkout.vertical.options.nutra.hint',
    icon: Leaf,
    kind: 'physical',
  },
  {
    id: 'smm',
    nameKey: 'experience.checkout.vertical.options.smm.name',
    hintKey: 'experience.checkout.vertical.options.smm.hint',
    icon: Users,
    kind: 'digital',
  },
  {
    id: 'clothing',
    nameKey: 'experience.checkout.vertical.options.clothing.name',
    hintKey: 'experience.checkout.vertical.options.clothing.hint',
    icon: Shirt,
    kind: 'physical',
  },
  {
    id: 'adult-ai',
    nameKey: 'experience.checkout.vertical.options.adult-ai.name',
    hintKey: 'experience.checkout.vertical.options.adult-ai.hint',
    icon: Sparkles,
    kind: 'any',
  },
  {
    id: 'others',
    nameKey: 'experience.checkout.vertical.options.others.name',
    hintKey: 'experience.checkout.vertical.options.others.hint',
    icon: MoreHorizontal,
    kind: 'any',
  },
];

/**
 * Verticals offered for a given store kind (the "Digital product" toggle in step 1). Digital stores
 * can't ship goods, so physical verticals are hidden; physical stores hide pure-digital verticals.
 * Catch-all (`any`) verticals are always shown.
 */
export function getSetupFunnelVerticalOptions(isDigital: boolean): SetupFunnelVerticalOption[] {
  const excludedKind: SetupFunnelVerticalKind = isDigital ? 'physical' : 'digital';
  return SETUP_FUNNEL_VERTICAL_OPTIONS.filter((option) => option.kind !== excludedKind);
}

/** Verticals that get the dark digital-product checkout theme. Everything else is normal. */
export const SETUP_FUNNEL_DARK_VERTICALS: ReadonlySet<SetupFunnelVertical> = new Set<SetupFunnelVertical>([
  'smm',
  'adult-ai',
]);

export function getSetupFunnelTheme(vertical: SetupFunnelVertical): SetupFunnelTheme {
  return SETUP_FUNNEL_DARK_VERTICALS.has(vertical) ? 'dark' : 'normal';
}

/** Studio checkout starter id per theme. */
export const SETUP_FUNNEL_CHECKOUT_STARTER_BY_THEME: Record<SetupFunnelTheme, string> = {
  dark: 'checkout-digital-product-dark',
  normal: 'checkout-page-v2',
};

/** Studio thank-you starter id per theme. */
export const SETUP_FUNNEL_THANKYOU_STARTER_BY_THEME: Record<SetupFunnelTheme, string> = {
  dark: 'checkout-thankyou-page-dark',
  normal: 'checkout-thankyou-page',
};

/** Studio offer/upsell starter id per vertical (used only when the merchant opts into an upsell). */
export const SETUP_FUNNEL_UPSELL_STARTER_BY_VERTICAL: Record<SetupFunnelVertical, string> = {
  smm: 'smm-growth-upsell-page',
  'adult-ai': 'ai-app-premium-upsell-page',
  peptides: 'peptides-offer-upsell-page',
  nutra: 'nutra-offer-upsell-page',
  clothing: 'clothing-offer-upsell-page',
  // "Others" catch-all → the catalog's default offer page (no niche-specific upsell).
  others: 'checkout-offer-page',
};

export function getSetupFunnelUpsellStarterId(vertical: SetupFunnelVertical): string {
  return SETUP_FUNNEL_UPSELL_STARTER_BY_VERTICAL[vertical];
}

/**
 * Funnel template that defines the step graph:
 * - `with-offer` → checkout → offer → thank-you
 * - `default`    → checkout → thank-you
 */
export function getSetupFunnelTemplateId(hasUpsell: boolean): string {
  return hasUpsell ? 'with-offer' : 'default';
}

/**
 * Per-step studio starter overrides for `useFunnelCreation`. Keyed by funnel step
 * type. Checkout + thank-you follow the theme; the offer step (when opted in)
 * follows the vertical.
 */
export function getSetupFunnelStarterOverrides(
  vertical: SetupFunnelVertical,
  hasUpsell: boolean,
): Record<string, string> {
  const theme = getSetupFunnelTheme(vertical);
  const overrides: Record<string, string> = {
    checkout: SETUP_FUNNEL_CHECKOUT_STARTER_BY_THEME[theme],
    thankyou: SETUP_FUNNEL_THANKYOU_STARTER_BY_THEME[theme],
  };
  if (hasUpsell) {
    const upsellStarterId = getSetupFunnelUpsellStarterId(vertical);
    // The `with-offer` template's upsell node is `type: 'offer'`; map `upsell`
    // too so the override still binds if that step type is ever renamed.
    overrides.offer = upsellStarterId;
    overrides.upsell = upsellStarterId;
  }
  return overrides;
}
